import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import SelectField from "../Masters/SelectField";
import TableSelectField from "./TableSelectField";

export const monthlist = [
  { id: 1, month: "Jan" },
  { id: 2, month: "Feb" },
  { id: 3, month: "Mar" },
  { id: 4, month: "Apr" },
  { id: 5, month: "May" },
  { id: 6, month: "Jun" },
  { id: 7, month: "Jul" },
  { id: 8, month: "Aug" },
  { id: 9, month: "Sep" },
  { id: 10, month: "Oct" },
  { id: 11, month: "Nov" },
  { id: 12, month: "Dec" },
];

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth(); // Current month (0-based, January = 0)
const startYear = currentYear - 5; // 5 years ago from the current year
const years = Array.from({ length: 6 }, (_, i) => startYear + i); // 6 years range (from 5 years ago to the current year)

const MonthYearPicker = ({
  name,
  title,
  errors,
  setErrors,
  formData,
  setFormData,
  defaultMonth = currentMonth,
  defaultYear = currentYear,
  width,
  gap,
  minDate,
  maxDate,
  disabled = false,
}) => {
  const [filteredYears, setFilteredYears] = useState(years);
  const [disableValue, setDisableValue] = useState("");

  const parseSupportedDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }
    if (typeof value === "string") {
      const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) {
        const d = new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]));
        return Number.isNaN(d.getTime()) ? null : d;
      }
      const dmyMatch = value.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
      if (dmyMatch) {
        const d = new Date(Number(dmyMatch[3]), Number(dmyMatch[2]) - 1, Number(dmyMatch[1]));
        return Number.isNaN(d.getTime()) ? null : d;
      }
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const minDateObj = useMemo(() => {
    return parseSupportedDate(minDate);
  }, [minDate]);

  const maxDateObj = useMemo(() => {
    return parseSupportedDate(maxDate);
  }, [maxDate]);

  const minYear = minDateObj ? minDateObj.getFullYear() : null;
  const maxYear = maxDateObj ? maxDateObj.getFullYear() : null;
  const minMonth = minDateObj ? minDateObj.getMonth() + 1 : null;
  const maxMonth = maxDateObj ? maxDateObj.getMonth() + 1 : null;
  const hasRangeConstraint = minYear != null && maxYear != null;

  useEffect(() => {
    if (minYear == null || maxYear == null) {
      setFilteredYears(years);
      return;
    }
    const dynamicYears = [];
    for (let year = minYear; year <= maxYear; year++) dynamicYears.push(year);
    setFilteredYears(dynamicYears);
  }, [minYear, maxYear]);

  // Get filtered months based on selected year
  const filteredMonths = useMemo(() => {
    const selectedYear = formData?.consumptionPeriod?.year;
    const selectedYearNum = Number(selectedYear);

    // If no explicit range is provided, keep old behavior for current year.
    let months = monthlist;
    if (
      !hasRangeConstraint &&
      (selectedYear === currentYear || selectedYear === String(currentYear))
    ) {
      months = monthlist.filter((month) => month.id <= currentMonth + 1);
    }

    if (hasRangeConstraint) {
      months = months.filter((month) => {
        if (selectedYearNum === minYear && month.id < minMonth) return false;
        if (selectedYearNum === maxYear && month.id > maxMonth) return false;
        return true;
      });
    }

    return months;
  }, [formData?.consumptionPeriod?.year, minYear, maxYear, minMonth, maxMonth]);

  // useEffect(() => {
  //   setFormData({
  //     ...formData,
  //     consumptionPeriod: {
  //       ...formData.consumptionPeriod,
  //       month: defaultMonth,
  //       year:defaultYear // Update the 'year' field within 'consumptionPeriod'
  //     },
  //   });
  // }, [defaultMonth, defaultYear]);

  // Handle Month and Year Change
  const handleMonthChange = (event) => {
    setDisableValue("");
    setErrors && setErrors({ ...errors, month: "" });
    const selectedMonthIndex = Number(event.target.value);
    console.log("month", monthlist[selectedMonthIndex - 1], selectedMonthIndex);
    setFormData({
      ...formData,
      consumptionPeriod: {
        ...formData.consumptionPeriod,
        month: selectedMonthIndex, // Update the 'month' field within 'consumptionPeriod'
      },
    });
    // Set the month index - if future month is selected, disable current year
    if (!hasRangeConstraint && selectedMonthIndex > currentMonth + 1) {
      setDisableValue(currentYear);
      const selectedYear = Number(formData?.consumptionPeriod?.year);
      if (selectedYear === currentYear) {
        setFormData({
          ...formData,
          consumptionPeriod: {
            ...formData.consumptionPeriod,
            year: "", // Clear year if invalid month selected for current year
          },
        });
      }
    }
  };

  const handleYearChange = (event) => {
    setDisableValue("");
    setErrors && setErrors({ ...errors, year: "" });
    const selectedYear = event.target.value;
    const selectedYearNum = Number(selectedYear);

    setFormData((prev) => {
      const prevMonthNum = Number(prev?.consumptionPeriod?.month);
      const hasMonth = Boolean(prevMonthNum);
      let shouldResetMonth = false;

      if (!hasRangeConstraint && selectedYearNum === currentYear) {
        if (hasMonth && prevMonthNum > currentMonth + 1) {
          shouldResetMonth = true;
        }
      }

      if (
        hasRangeConstraint &&
        hasMonth &&
        ((selectedYearNum === minYear && prevMonthNum < minMonth) ||
          (selectedYearNum === maxYear && prevMonthNum > maxMonth))
      ) {
        shouldResetMonth = true;
      }

      return {
        ...prev,
        consumptionPeriod: {
          ...prev.consumptionPeriod,
          year: selectedYear,
          month: shouldResetMonth ? "" : prev.consumptionPeriod?.month,
        },
      };
    });
  };

  return (
    <Box>
      {title && (
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: "bold",
            marginBottom:
              title === "Consumption Month" ||
              title === "Purchased Month" ||
              title === "Waste Management Month"
                ? 0
                : "-0.5rem",
          }}
        >
          {title}
        </Typography>
      )}
      <Box sx={{ display: "flex", gap: gap || 1 }}>
        <Box sx={{ minWidth: "48%" }}>
          <SelectField
            name={name}
            value={formData?.consumptionPeriod?.month}
            handleChange={handleMonthChange}
            label="Month"
            field="month"
            errors={errors?.["month"] || ""}
            defaultValue={"Month"}
            options={filteredMonths} // Filtered month options based on selected year
            disable={disabled}
          />
        </Box>

        {/* Year Input */}
        <Box sx={{ minWidth: "48%" }}>
          <SelectField
            name={name}
            value={formData?.consumptionPeriod?.year}
            handleChange={handleYearChange}
            options={filteredYears}
            errors={errors?.["year"] || ""}
            defaultValue={"Year"}
            menuDisable={disableValue}
            disable={disabled}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default MonthYearPicker;
